import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';

class VideoProcessor {
  constructor() {
    this.ffmpeg = new FFmpeg();
    this.isLoaded = false;
    this.isProcessing = false;
    this.currentProgress = 0;
    this.onProgress = null;
    this.onComplete = null;
    this.onError = null;
  }

  async load() {
    if (this.isLoaded) return;
    
    try {
      // Load FFmpeg with the core files
      const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.10/dist/umd';
      await this.ffmpeg.load({
        coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
        wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'text/wasm'),
      });
      this.isLoaded = true;
      console.log('FFmpeg loaded successfully');
    } catch (error) {
      console.error('Failed to load FFmpeg:', error);
      throw new Error('Failed to load video processing library');
    }
  }

  async transcodeVideo(inputFile, options = {}) {
    if (this.isProcessing) {
      throw new Error('Video processing is already in progress');
    }

    if (!this.isLoaded) {
      await this.load();
    }

    this.isProcessing = true;
    this.currentProgress = 0;

    try {
      // Set up progress monitoring
      this.ffmpeg.on('progress', ({ progress }) => {
        this.currentProgress = Math.round(progress * 100);
        if (this.onProgress) {
          this.onProgress(this.currentProgress);
        }
      });

      // Write input file to FFmpeg filesystem
      const inputFileName = 'input.mp4';
      const outputFileName = 'output.mp4';
      
      await this.ffmpeg.writeFile(inputFileName, await fetchFile(inputFile));

      // Get video dimensions to calculate scaling
      const videoInfo = await this.getVideoInfo(inputFile);
      const { width, height } = videoInfo;
      
      // Calculate new dimensions (smaller dimension should be 1080px)
      const maxDimension = 1080;
      let newWidth = width;
      let newHeight = height;
      
      if (width > height) {
        // Landscape video
        if (height > maxDimension) {
          newHeight = maxDimension;
          newWidth = Math.round((width * maxDimension) / height);
        }
      } else {
        // Portrait video
        if (width > maxDimension) {
          newWidth = maxDimension;
          newHeight = Math.round((height * maxDimension) / width);
        }
      }

      // Ensure dimensions are even numbers (required by some codecs)
      newWidth = newWidth % 2 === 0 ? newWidth : newWidth - 1;
      newHeight = newHeight % 2 === 0 ? newHeight : newHeight - 1;

      // Build FFmpeg command
      const bitrate = options.bitrate || '10M'; // 10 Mbps default
      const command = [
        '-i', inputFileName,
        '-c:v', 'libx264',
        '-b:v', bitrate,
        '-c:a', 'aac',
        '-b:a', '128k',
        '-vf', `scale=${newWidth}:${newHeight}`,
        '-preset', 'medium',
        '-crf', '23',
        '-movflags', '+faststart',
        '-t', '30', // Cut video to 30 seconds maximum
        '-y', // Overwrite output file
        outputFileName
      ];

      console.log('Transcoding video with command:', command.join(' '));
      console.log(`Original: ${width}x${height}, New: ${newWidth}x${newHeight}, Duration: max 30 seconds`);

      // Execute FFmpeg command
      await this.ffmpeg.exec(command);

      // Read the output file
      const data = await this.ffmpeg.readFile(outputFileName);
      
      // Clean up files
      await this.ffmpeg.deleteFile(inputFileName);
      await this.ffmpeg.deleteFile(outputFileName);

      // Convert to Blob
      const blob = new Blob([data.buffer], { type: 'video/mp4' });
      
      if (this.onComplete) {
        this.onComplete(blob);
      }

      return blob;

    } catch (error) {
      console.error('Video transcoding failed:', error);
      if (this.onError) {
        this.onError(error);
      }
      throw error;
    } finally {
      this.isProcessing = false;
    }
  }

  async getVideoInfo(file) {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      
      video.onloadedmetadata = () => {
        resolve({
          width: video.videoWidth,
          height: video.videoHeight,
          duration: video.duration
        });
        URL.revokeObjectURL(video.src);
      };
      
      video.onerror = () => {
        reject(new Error('Failed to load video metadata'));
        URL.revokeObjectURL(video.src);
      };
      
      video.src = URL.createObjectURL(file);
    });
  }

  setProgressCallback(callback) {
    this.onProgress = callback;
  }

  setCompleteCallback(callback) {
    this.onComplete = callback;
  }

  setErrorCallback(callback) {
    this.onError = callback;
  }

  getProgress() {
    return this.currentProgress;
  }

  isCurrentlyProcessing() {
    return this.isProcessing;
  }

  async cancel() {
    if (this.isProcessing) {
      try {
        await this.ffmpeg.terminate();
        this.isProcessing = false;
        this.currentProgress = 0;
        console.log('Video processing cancelled');
      } catch (error) {
        console.error('Error cancelling video processing:', error);
      }
    }
  }

  async reset() {
    try {
      // Terminate if currently processing
      if (this.isProcessing) {
        await this.ffmpeg.terminate();
      }
      
      // Reset all state
      this.isLoaded = false;
      this.isProcessing = false;
      this.currentProgress = 0;
      this.onProgress = null;
      this.onComplete = null;
      this.onError = null;
      
      // Create a new FFmpeg instance
      this.ffmpeg = new FFmpeg();
      
      console.log('Video processor reset successfully');
    } catch (error) {
      console.error('Error resetting video processor:', error);
    }
  }
}

// Create a singleton instance
const videoProcessor = new VideoProcessor();

export default videoProcessor;
